const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);

const smoothstep = (edge0, edge1, x) => {
  const t = clamp((x - edge0) / (edge1 - edge0 || 1e-6), 0, 1);
  return t * t * (3 - 2 * t);
};

const readNumber = (el, name, fallback) => {
  const raw = el.getAttribute(name);
  if (raw == null || raw === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
};

const initScrollExpand = (root) => {
  if (root.dataset.scrollExpandReady === 'true') return;
  root.dataset.scrollExpandReady = 'true';

  const track = root.querySelector('.scroll-expand__track');
  const stage = root.querySelector('.scroll-expand__stage');
  const frame = root.querySelector('.scroll-expand__frame');
  const media = root.querySelector('.scroll-expand__media');
  const intro = root.querySelector('.scroll-expand__title--intro');
  const title = root.querySelector('.scroll-expand__title--main');
  const overlay = root.querySelector('.scroll-expand__overlay');
  const scrim = root.querySelector('.scroll-expand__scrim');
  const hint = root.querySelector('.scroll-expand__hint');

  if (!track || !stage || !frame || !media) return;

  const props = {
    startWidth: readNumber(root, 'data-start-width', 42),
    startHeight: readNumber(root, 'data-start-height', 58),
    startRadius: readNumber(root, 'data-start-radius', 24),
    endRadius: readNumber(root, 'data-end-radius', 0),
    mediaZoom: readNumber(root, 'data-media-zoom', 1.35),
    scrollDistance: readNumber(root, 'data-scroll-distance', 0.75),
    holdDistance: readNumber(root, 'data-hold-distance', 0.35),
    smoothing: readNumber(root, 'data-smoothing', 0.1),
    overlayScrim: readNumber(root, 'data-overlay-scrim', 0.35),
    mediaBlur: readNumber(root, 'data-media-blur', 18),
  };

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  let stageH = 0;
  let raf = 0;
  let current = 0;
  let target = 0;
  let running = false;

  const applyProgress = (p) => {
    const t = clamp(p, 0, 1);
    const e = smoothstep(0.2, 0.94, t);

    const w = props.startWidth + (100 - props.startWidth) * e;
    const h = props.startHeight + (100 - props.startHeight) * e;
    const ix = Math.max(0, (100 - w) / 2);
    const iy = Math.max(0, (100 - h) / 2);
    const r = props.startRadius + (props.endRadius - props.startRadius) * e;

    frame.style.clipPath = `inset(${iy}% ${ix}% ${iy}% ${ix}% round ${r}px)`;
    media.style.transform = `scale(${props.mediaZoom + (1 - props.mediaZoom) * e})`;

    const clear = reduceMotion ? 1 : smoothstep(0.02, 0.5, t);
    const blur = props.mediaBlur * (1 - clear);
    media.style.filter = blur < 0.2 ? 'none' : `blur(${blur}px)`;

    if (scrim) scrim.style.opacity = `${props.overlayScrim * e}`;

    if (intro) {
      const out = smoothstep(0.1, 0.32, t);
      intro.style.opacity = `${1 - out}`;
      intro.style.transform = `translate3d(0, ${-22 * out}px, 0) scale(${1 + 0.04 * out})`;
    }

    if (title) {
      const inn = smoothstep(0.2, 0.38, t);
      const out = smoothstep(0.58, 0.82, t);
      const vis = inn * (1 - out);
      title.style.opacity = `${vis}`;
      title.style.transform = `translate3d(0, ${14 * (1 - inn) - 22 * out}px, 0) scale(${0.98 + 0.02 * inn + 0.04 * out})`;
    }

    if (hint) {
      const gone = smoothstep(0, 0.08, t);
      hint.style.opacity = `${1 - gone}`;
      hint.style.transform = `translate3d(0, ${8 * gone}px, 0)`;
    }

    if (overlay) {
      const inn = smoothstep(0.72, 0.96, t);
      overlay.style.opacity = `${inn}`;
      overlay.style.transform = `translate3d(0, ${18 * (1 - inn)}px, 0)`;
      overlay.style.pointerEvents = inn > 0.85 ? 'auto' : 'none';
    }
  };

  const span = () => Math.max(80, stageH * Math.max(0.35, props.scrollDistance));

  const measure = () => {
    stageH = root.clientHeight || window.innerHeight;
    if (stageH <= 0) return;
    stage.style.height = `${stageH}px`;
    track.style.height = `${stageH + span() + stageH * Math.max(0, props.holdDistance)}px`;
    stage.style.setProperty('--se-title-size', `${clamp((root.clientWidth || stageH) * 0.062, 22, 72)}px`);
  };

  const readProgress = () => clamp(root.scrollTop / span(), 0, 1);

  const tick = () => {
    const k = props.smoothing <= 0 ? 1 : 1 - Math.exp(-1 / (60 * props.smoothing));
    current += (target - current) * k;
    if (Math.abs(target - current) < 0.00015) {
      current = target;
      running = false;
    }
    applyProgress(current);
    raf = running ? requestAnimationFrame(tick) : 0;
  };

  const kick = () => {
    if (running) return;
    running = true;
    if (!raf) raf = requestAnimationFrame(tick);
  };

  const onScroll = () => {
    target = readProgress();
    if (props.smoothing <= 0 || reduceMotion) {
      current = target;
      applyProgress(current);
      return;
    }
    kick();
  };

  measure();
  target = readProgress();
  current = target;
  applyProgress(current);

  root.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', () => {
    const p = readProgress();
    measure();
    root.scrollTop = p * span();
    target = readProgress();
    current = target;
    applyProgress(current);
  });
};

const start = () => {
  document.querySelectorAll('[data-scroll-expand]').forEach(initScrollExpand);
};

window.initLoleScrollExpand = start;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start);
} else {
  start();
}
